import { ECurrency } from '@/modules/currenciesModule/constants/currencies'

// Singleton — cookie refs and caches shared across all formatPrice calls
const usePriceState = createGlobalState(() => {
	const currencyCookie = useCookie('currency')
	const resultCache = new Map<string, string | undefined>()
	const plFormatters = new Map<number, Intl.NumberFormat>()
	const enFormatters = new Map<number, Intl.NumberFormat>()

	watch(currencyCookie, () => resultCache.clear())

	const getPlFormatter = (fd: number) => {
		if (!plFormatters.has(fd)) plFormatters.set(fd, new Intl.NumberFormat('pl-PL', { minimumFractionDigits: fd, maximumFractionDigits: fd }))
		return plFormatters.get(fd)!
	}

	const getEnFormatter = (fd: number) => {
		if (!enFormatters.has(fd)) enFormatters.set(fd, new Intl.NumberFormat('en-US', { minimumFractionDigits: fd, maximumFractionDigits: fd }))
		return enFormatters.get(fd)!
	}

	return { currencyCookie, resultCache, getPlFormatter, getEnFormatter }
})

export const formatPrice = (
	number: number | string | undefined,
	fractionDigits = 2,
	returnOnlyNumberWithCurrency = false,
	bypassCurrency?: ECurrency,
) => {
	if (!number && number !== 0 && number !== '0') return number

	const { currencyCookie, resultCache, getPlFormatter } = usePriceState()
	const currency = bypassCurrency || (currencyCookie.value as ECurrency) || ECurrency.PLN

	const cacheKey = `${number}|${fractionDigits}|${returnOnlyNumberWithCurrency ? 1 : 0}|${currency}`
	if (resultCache.has(cacheKey)) return resultCache.get(cacheKey)

	const currencyObj = CurrenciesMap[currency]
	const currencyInFront = currencyObj?.position === ECurrencySymbolPosition.FRONT ? CurrenciesMap[currency].symbol : ''
	const currencyInBack = currencyObj?.position === ECurrencySymbolPosition.BACK ? CurrenciesMap[currency].symbol : ''
	const fmt = getPlFormatter(fractionDigits)

	let result: string
	if (typeof number === 'number') {
		result = `${currencyInFront}${fmt.format(number)} ${currencyInBack}`
	} else {
		const parsed = parseFloat(number.replace(/,/g, ''))
		if (!isNaN(parsed)) {
			result = `${currencyInFront}${fmt.format(parsed)} ${currencyInBack}`
		} else {
			resultCache.set(cacheKey, number)
			return number
		}
	}

	resultCache.set(cacheKey, result)
	return result
}

export const formatPriceInReservationCurrency = (number: number | string | undefined, fractionDigits = 2, currency = ECurrency.PLN) => {
	if (!number && number !== 0 && number !== '0') return number

	const { resultCache, getEnFormatter } = usePriceState()

	const cacheKey = `res|${number}|${fractionDigits}|${currency}`
	if (resultCache.has(cacheKey)) return resultCache.get(cacheKey)

	const currencyObj = CurrenciesMap[currency]
	const currencyInFront = currencyObj?.position === ECurrencySymbolPosition.FRONT ? CurrenciesMap[currency].symbol : ''
	const currencyInBack = currencyObj?.position === ECurrencySymbolPosition.BACK ? CurrenciesMap[currency].symbol : ''
	const fmt = getEnFormatter(fractionDigits)

	let result: string
	if (typeof number === 'number') {
		result = `${currencyInFront}${fmt.format(number)} ${currencyInBack}`
	} else {
		const parsed = parseFloat(number.replace(/,/g, ''))
		if (!isNaN(parsed)) {
			result = `${currencyInFront}${fmt.format(parsed)} ${currencyInBack}`
		} else {
			resultCache.set(cacheKey, number)
			return number
		}
	}

	resultCache.set(cacheKey, result)
	return result
}
